import { type RefObject, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKey } from '../../../constant/queryKey';
import { marketGoodsListFilterAtom } from '../states/GoodsListFilter.atom';
import { useAtomValue } from 'jotai';
import { useMarketRoute } from '../../../hooks/useMarketRoute';
import { isNotNil } from 'shared/src/utils';
import type { ListRef } from '@banhala/ui-kit';
import type { MarketHomeContentsKeys } from '../types/MarketHomeContents';
import { useSafeTimeout } from 'shared/src/hooks';

export const useScrollToGoodsList = (
  ref: RefObject<ListRef<MarketHomeContentsKeys> | null>,
  categoriesPositionY: number,
) => {
  const client = useQueryClient();
  const { params } = useMarketRoute<'MarketHome'>();
  const filter = useAtomValue(marketGoodsListFilterAtom);
  const [scrollOnce, setScrollOnce] = useState<boolean>(
    !params.extras?.order && !params.extras?.category_sno,
  );
  const [needScroll, setNeedScroll] = useState<boolean>(false);
  const setSafeTimeout = useSafeTimeout();
  useEffect(() => {
    const queryCache = client.getQueryCache();
    const unsubscribe = queryCache.subscribe(event => {
      const cachedQueryKey = JSON.stringify(event.query.queryKey);

      if (
        event.type === 'updated' &&
        (cachedQueryKey ===
          JSON.stringify(
            queryKey.marketGoodsList(params.marketId, filter[params.marketId]),
          ) ||
          cachedQueryKey ===
            JSON.stringify(queryKey.marketGoodsCarousel(params.marketId)))
      ) {
        const goods = client.getQueryState(
          queryKey.marketGoodsList(params.marketId, filter[params.marketId]),
        );
        const carousels = client.getQueryState(
          queryKey.marketGoodsCarousel(params.marketId),
        );
        if (goods?.status === 'success' && carousels?.status === 'success') {
          if (isNotNil(params.extras) && !scrollOnce) {
            setSafeTimeout(() => {
              setScrollOnce(true);
              setNeedScroll(true);
            }, 10);
          }
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [
    categoriesPositionY,
    client,
    filter,
    params.extras,
    params.marketId,
    ref,
    scrollOnce,
    setSafeTimeout,
  ]);

  useEffect(() => {
    if (needScroll) {
      ref.current?.scrollToOffset({
        animated: true,
        offset: categoriesPositionY - 102,
      });
      setNeedScroll(false);
    }
  }, [categoriesPositionY, needScroll, ref]);
};
